/**
 * Step 21B — display metadata for the concern filter pills.
 *
 * Stable order matters: the first 8 entries are the default-
 * visible pills; the remaining 5 hide behind the "More +"
 * expander. Don't reorder without updating the test.
 *
 * Icons + accent colors are display-only — clinical relevance
 * lives in concern-markers.ts (the per-concern marker map).
 */

import {
  Activity,
  BatteryCharging,
  Brain,
  CircleDashed,
  Clock,
  Flame,
  Heart,
  HeartPulse,
  Leaf,
  Moon,
  ShieldCheck,
  Sparkles,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import type { PrimaryConcern } from '@/lib/health-profile/types';

export interface ConcernMeta {
  key: PrimaryConcern;
  label: string;
  icon: LucideIcon;
  /** Tailwind background class for the icon circle (unselected). */
  iconBg: string;
  /** Tailwind text color for the icon (unselected). */
  iconText: string;
}

/** First 8: default-visible pills. Last 5: behind "More +". */
export const CONCERN_META: ReadonlyArray<ConcernMeta> = [
  { key: 'energy',            label: 'Energy',            icon: BatteryCharging, iconBg: 'bg-amber-100',    iconText: 'text-amber-700' },
  { key: 'heart',             label: 'Heart',             icon: HeartPulse,      iconBg: 'bg-rose-100',     iconText: 'text-rose-700' },
  { key: 'cognition',         label: 'Cognition',         icon: Brain,           iconBg: 'bg-violet-100',   iconText: 'text-violet-700' },
  { key: 'mood',              label: 'Mood',              icon: Heart,           iconBg: 'bg-pink-100',     iconText: 'text-pink-700' },
  { key: 'sleep',             label: 'Sleep',             icon: Moon,            iconBg: 'bg-indigo-100',   iconText: 'text-indigo-700' },
  { key: 'hormones',          label: 'Hormones',          icon: Sparkles,        iconBg: 'bg-fuchsia-100',  iconText: 'text-fuchsia-700' },
  { key: 'immune',            label: 'Immune',            icon: ShieldCheck,     iconBg: 'bg-emerald-100',  iconText: 'text-emerald-700' },
  { key: 'longevity',         label: 'Longevity',         icon: Clock,           iconBg: 'bg-sky-100',      iconText: 'text-sky-700' },
  { key: 'weight',            label: 'Weight',            icon: Activity,        iconBg: 'bg-orange-100',   iconText: 'text-orange-700' },
  { key: 'digestion',         label: 'Digestion',         icon: CircleDashed,    iconBg: 'bg-lime-100',     iconText: 'text-lime-700' },
  { key: 'skin',              label: 'Skin',              icon: Sun,             iconBg: 'bg-yellow-100',   iconText: 'text-yellow-700' },
  { key: 'inflammation',      label: 'Inflammation',      icon: Flame,           iconBg: 'bg-red-100',      iconText: 'text-red-700' },
  { key: 'wellness_baseline', label: 'Wellness baseline', icon: Leaf,            iconBg: 'bg-teal-100',     iconText: 'text-teal-700' },
];

export const DEFAULT_VISIBLE_COUNT = 8;
export const CONCERNS_PARAM = 'concerns';

/** Parse `?concerns=heart,energy` into a typed array, dropping
 *  any unknown values silently. */
export function parseConcerns(raw: string | null): PrimaryConcern[] {
  if (!raw) return [];
  const known = new Set<string>(CONCERN_META.map((m) => m.key));
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => known.has(s)) as PrimaryConcern[];
}

/** Stringify back to the URL form. Empty array → null so the
 *  caller can drop the param entirely. */
export function serializeConcerns(concerns: readonly PrimaryConcern[]): string | null {
  if (concerns.length === 0) return null;
  // Preserve declared order for stable URLs across re-clicks.
  const order = new Map(CONCERN_META.map((m, i) => [m.key, i]));
  return [...concerns]
    .sort((a, b) => (order.get(a) ?? 99) - (order.get(b) ?? 99))
    .join(',');
}
